import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, UserPlus, Shield, Activity, Calendar, Mail, Eye, X, Building, Globe, Phone, DollarSign, Users as UsersIcon, LogIn, BarChart3, Bell, Trash2, LogOut, User } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/AdminSidebar';
import { notificationService } from '../../services/notificationService';
import SupportTicketsTab from './components/SupportTicketsTab';
import WhiteLabelTab from './components/WhiteLabelTab';


export default function AdminDashboard() {
  const navigate = useNavigate();
  const { profile, refreshProfile, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams?.get('tab') || 'dashboard';
  
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [regularUsers, setRegularUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    adminCount: 0,
    regularCount: 0,
    activeUsers: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [creating, setCreating] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (initialLoading) {
      checkAdminAndLoadData();
    } else {
      loadTabData();
    }
  }, [activeTab]);

  const checkAdminAndLoadData = async () => {
    try {
      setInitialLoading(true);
      
      // Check if user is admin
      const isAdmin = await adminService?.isAdmin();
      if (!isAdmin) {
        navigate('/');
        return;
      }

      // Load initial data
      await loadTabData();
    } catch (err) {
      setError(err?.message || 'Failed to load admin dashboard');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadTabData = async () => {
    try {
      setTabLoading(true);
      setError('');

      // Load data based on active tab
      if (activeTab === 'admins') {
        await Promise.all([loadAdminUsers(), loadStats()]);
      } else if (activeTab === 'dashboard') {
        await Promise.all([loadRegularUsers(), loadStats(), loadChartData()]);
      }
      // For 'notifications' and 'support' tabs, components handle their own loading
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setTabLoading(false);
    }
  };

  const loadRegularUsers = async () => {
    try {
      const data = await adminService?.getRegularUsers();
      setRegularUsers(data);
    } catch (err) {
      console.error('Error loading regular users:', err);
      throw err;
    }
  };

  const loadAdminUsers = async () => {
    try {
      const data = await adminService?.getAdminUsers();
      setAdminUsers(data);
    } catch (err) {
      console.error('Error loading admin users:', err);
      throw err;
    }
  };

  const loadChartData = async () => {
    try {
      const data = await adminService?.getSevenDayActivity();
      setChartData(data);
    } catch (err) {
      console.error('Error loading chart data:', err);
      throw err;
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminService?.getUserStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
      throw err;
    }
  };

  const handleCreateAdmin = async (e) => {
    e?.preventDefault();
    setCreating(true);
    setError('');

    try {
      await adminService?.createAdminUser(newAdmin);
      setShowCreateModal(false);
      setNewAdmin({ email: '', password: '', fullName: '' });
      await loadAdminUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to create admin user');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const confirmed = window.confirm(
      `Are you sure you want to change this user's role to ${newRole}?`
    );

    if (!confirmed) return;

    try {
      await adminService?.updateUserRole(userId, newRole);
      
      // Reload data based on current tab
      if (activeTab === 'admins') {
        await loadAdminUsers();
      } else {
        await loadRegularUsers();
      }
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to update user role');
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleLoginAsUser = async (userId, userEmail) => {
    setImpersonateTarget({ userId, userEmail });
    setShowImpersonateModal(true);
  };

  const confirmImpersonation = async () => {
    if (!impersonateTarget) return;

    try {
      setImpersonating(true);
      setError('');
      
      const result = await adminService?.impersonateUser(impersonateTarget?.userId);
      
      if (result?.success) {
        setShowImpersonateModal(false);
        setImpersonateTarget(null);
        await refreshProfile();
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Failed to impersonate user');
      setImpersonating(false);
    }
  };

  const cancelImpersonation = () => {
    setShowImpersonateModal(false);
    setImpersonateTarget(null);
    setImpersonating(false);
  };

  const handleDeleteUser = (user) => {
    setDeleteTarget(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      setError('');

      const result = await adminService?.deleteUser(deleteTarget?.id);

      if (result?.success) {
        setShowDeleteModal(false);
        setDeleteTarget(null);
        
        // Reload data based on current tab
        if (activeTab === 'admins') {
          await loadAdminUsers();
        } else {
          await loadRegularUsers();
        }
        await loadStats();
        
        // Show success message
        alert(`Successfully deleted user ${deleteTarget?.email} and all associated data`);
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete user');
      setDeleting(false);
    } finally {
      if (!error) {
        setDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleting(false);
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        setLoggingOut(true);
        await signOut();
        navigate('/admin-login', { replace: true });
      } catch (error) {
        console.error('Logout error:', error);
        setError('Failed to logout. Please try again.');
      } finally {
        setLoggingOut(false);
      }
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen ml-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const currentUsers = activeTab === 'admins' ? adminUsers : regularUsers;

  return (
    <>
      <AdminSidebar />
      <div className="min-h-screen bg-gray-50 ml-64">
        <div className="p-6">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {activeTab === 'admins' ? 'Manage Admins' : 
                   activeTab === 'notifications' ? 'Manage Notifications' : 
                   activeTab === 'support' ? 'Support Tickets' : 
                   activeTab === 'branding'? 'White Label Branding' : 'Admin Dashboard'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {activeTab === 'admins' ? 'Manage admin users and permissions' : 
                   activeTab === 'notifications' ? 'Create and manage user notifications' : 
                   activeTab === 'support' ? 'View and manage user support tickets' : 
                   activeTab === 'branding' ? 'Customize platform branding and appearance' :
                   'Monitor users and system metrics'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Admin Profile Display */}
                {profile && (
                  <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200">
                    {profile?.avatarUrl ? (
                      <img
                        className="h-8 w-8 rounded-full"
                        src={profile?.avatarUrl}
                        alt={profile?.fullName}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                        {profile?.fullName?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{profile?.fullName}</span>
                      <span className="text-xs text-gray-500">Admin</span>
                    </div>
                  </div>
                )}

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  title="Logout"
                >
                  {loggingOut ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Logging out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </>
                  )}
                </button>

                {activeTab === 'admins' && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <UserPlus className="w-5 h-5" />
                    Create Admin
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="max-w-7xl mx-auto mb-6">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <SupportTicketsTab />
          )}

          {activeTab === 'branding' && (
            <WhiteLabelTab />
          )}

          {activeTab === 'dashboard' && (
            <>
              {/* Stats Cards */}
              <div className="max-w-7xl mx-auto mb-8">
                {tabLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Total Users</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalUsers}</p>
                        </div>
                        <Users className="w-12 h-12 text-blue-500" />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Admins</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.adminCount}</p>
                        </div>
                        <Shield className="w-12 h-12 text-purple-500" />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Regular Users</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.regularCount}</p>
                        </div>
                        <Users className="w-12 h-12 text-green-500" />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Active (7 days)</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.activeUsers}</p>
                        </div>
                        <Activity className="w-12 h-12 text-orange-500" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 7-Day Activity Chart */}
              <div className="max-w-7xl mx-auto mb-8">
                {tabLoading ? (
                  <div className="bg-white rounded-lg shadow p-6 flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <BarChart3 className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">7-Day User Activity</h2>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="newUsers" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          name="New Users" 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="logins" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          name="User Logins" 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <NotificationManagement />
          )}

          {(activeTab === 'dashboard' || activeTab === 'admins') && (
            /* Users Table */
            <div className="max-w-7xl mx-auto">
              {tabLoading ? (
                <div className="bg-white rounded-lg shadow p-6 flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {activeTab === 'admins' ? 'Admin Users' : 'Regular Users'}
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Registered
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentUsers?.map((user) => (
                          <tr key={user?.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  {user?.avatarUrl ? (
                                    <img
                                      className="h-10 w-10 rounded-full"
                                      src={user?.avatarUrl}
                                      alt={user?.fullName}
                                    />
                                  ) : (
                                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                      {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{user?.fullName}</div>
                                  <div className="text-sm text-gray-500">{user?.businessName || 'N/A'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-900">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                {user?.email}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                user?.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {user?.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="w-4 h-4 mr-2" />
                                {formatDate(user?.createdAt)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {formatDate(user?.lastLogin)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewDetails(user)}
                                  className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="text-sm">Details</span>
                                </button>
                                
                                {user?.role === 'user' && (
                                  <button
                                    onClick={() => handleLoginAsUser(user?.id, user?.email)}
                                    disabled={impersonating}
                                    className="text-purple-600 hover:text-purple-900 flex items-center gap-1 disabled:opacity-50"
                                    title="Login as User"
                                  >
                                    <LogIn className="w-4 h-4" />
                                    <span className="text-sm">Login As</span>
                                  </button>
                                )}

                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  disabled={deleting || user?.id === profile?.id}
                                  className="text-red-600 hover:text-red-900 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={user?.id === profile?.id ? "Cannot delete your own account" : "Delete User"}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span className="text-sm">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Details Modal */}
          {showDetailsModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">User Account Details</h2>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedUser(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Profile Header */}
                  <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
                    <div className="flex-shrink-0">
                      {selectedUser?.avatarUrl ? (
                        <img
                          className="h-20 w-20 rounded-full"
                          src={selectedUser?.avatarUrl}
                          alt={selectedUser?.fullName}
                        />
                      ) : (
                        <div className="h-20 w-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-semibold">
                          {selectedUser?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedUser?.fullName}</h3>
                      <p className="text-gray-600">{selectedUser?.email}</p>
                      <span className={`inline-block mt-2 px-3 py-1 text-sm font-semibold rounded-full ${
                        selectedUser?.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {selectedUser?.role}
                      </span>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5" />
                      Business Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Business Name</label>
                        <p className="text-gray-900">{selectedUser?.businessName || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Domain</label>
                        <p className="text-gray-900 flex items-center gap-1">
                          <Globe className="w-4 h-4 text-gray-400" />
                          {selectedUser?.domain || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
                        <p className="text-gray-900">{selectedUser?.country || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Currency</label>
                        <p className="text-gray-900">{selectedUser?.currency || 'ZAR'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                        <p className="text-gray-900">{selectedUser?.phoneNumber || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-gray-900 flex items-center gap-1">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {selectedUser?.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Metrics */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Business Metrics
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Employee Count</label>
                        <p className="text-gray-900 flex items-center gap-1">
                          <UsersIcon className="w-4 h-4 text-gray-400" />
                          {selectedUser?.employeeCount || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Monthly Revenue</label>
                        <p className="text-gray-900">{formatCurrency(selectedUser?.monthlyRevenue)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Account Activity */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Account Activity
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Registration Date</label>
                        <p className="text-gray-900 flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(selectedUser?.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Last Login</label>
                        <p className="text-gray-900 flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(selectedUser?.lastLogin)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Account Updated</label>
                        <p className="text-gray-900">{formatDate(selectedUser?.updatedAt)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">User ID</label>
                        <p className="text-gray-900 text-xs font-mono">{selectedUser?.id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-6 border-t border-gray-200 flex gap-3">
                    {selectedUser?.role === 'user' && (
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          handleLoginAsUser(selectedUser?.id, selectedUser?.email);
                        }}
                        disabled={impersonating}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                      >
                        <LogIn className="w-5 h-5" />
                        Login as This User
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setSelectedUser(null);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Admin Modal */}
          {showCreateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Create Admin User</h2>
                <form onSubmit={handleCreateAdmin}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        required
                        value={newAdmin?.fullName}
                        onChange={(e) => setNewAdmin({ ...newAdmin, fullName: e?.target?.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={newAdmin?.email}
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e?.target?.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        required
                        value={newAdmin?.password}
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e?.target?.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Min 6 characters"
                        minLength={6}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setNewAdmin({ email: '', password: '', fullName: '' });
                        setError('');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={creating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      disabled={creating}
                    >
                      {creating ? 'Creating...' : 'Create Admin'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Impersonation Confirmation Modal */}
          {showImpersonateModal && impersonateTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirm User Impersonation</h2>
                
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to login as <span className="font-semibold">{impersonateTarget?.userEmail}</span>?
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> You will see the application from this user's perspective. All actions will be performed as this user.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={cancelImpersonation}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={impersonating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmImpersonation}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={impersonating}
                  >
                    {impersonating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Starting...</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        <span>Confirm Impersonation</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete User Confirmation Modal */}
          {showDeleteModal && deleteTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Delete User Account</h2>
                </div>
                
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to permanently delete the account for <span className="font-semibold">{deleteTarget?.fullName}</span> ({deleteTarget?.email})?
                  </p>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800 font-semibold mb-2">
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="text-sm text-red-700">
                      This will permanently delete:
                    </p>
                    <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                      <li>User account and profile</li>
                      <li>All projects and tasks</li>
                      <li>All financial data and calculations</li>
                      <li>All budgets and forecasts</li>
                      <li>All support tickets</li>
                      <li>All assets and configurations</li>
                      <li>All other associated data</li>
                    </ul>
                  </div>

                  {deleteTarget?.role === 'admin' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> You are about to delete an admin account. Make sure there are other admin accounts available to manage the system.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        <span>Delete Permanently</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Notification Management Component
function NotificationManagement() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    expiresAt: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService?.getAllNotifications();
      setNotifications(data);
    } catch (err) {
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingNotification) {
        await notificationService?.updateNotification(editingNotification?.id, formData);
      } else {
        await notificationService?.createNotification(formData);
      }
      
      setShowCreateModal(false);
      setEditingNotification(null);
      setFormData({ title: '', message: '', type: 'info', expiresAt: '' });
      await loadNotifications();
    } catch (err) {
      setError(err?.message || 'Failed to save notification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification?.title,
      message: notification?.message,
      type: notification?.type,
      expiresAt: notification?.expiresAt || ''
    });
    setShowCreateModal(true);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await notificationService?.updateNotification(id, { isActive: !currentStatus });
      await loadNotifications();
    } catch (err) {
      setError(err?.message || 'Failed to update notification');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    
    try {
      await notificationService?.deleteNotification(id);
      await loadNotifications();
    } catch (err) {
      setError(err?.message || 'Failed to delete notification');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiration';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="mb-6 flex justify-between items-center">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setEditingNotification(null);
            setFormData({ title: '', message: '', type: 'info', expiresAt: '' });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Bell className="w-5 h-5" />
          Create Notification
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {notifications?.map((notification) => (
                <tr key={notification?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{notification?.title}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 max-w-md truncate">{notification?.message}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      notification?.type === 'error' ? 'bg-red-100 text-red-800' :
                      notification?.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      notification?.type === 'success'? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {notification?.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      notification?.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {notification?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(notification?.expiresAt)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(notification)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(notification?.id, notification?.isActive)}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        {notification?.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(notification?.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {editingNotification ? 'Edit Notification' : 'Create Notification'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData?.title}
                    onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notification title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    required
                    value={formData?.message}
                    onChange={(e) => setFormData({ ...formData, message: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notification message"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData?.type}
                    onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData?.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingNotification(null);
                    setFormData({ title: '', message: '', type: 'info', expiresAt: '' });
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingNotification ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}